import TimeScheduleChart from "../../timeschedulechart/TimeScheduleChart.js";
import ComboBoxWithMessages from "../../../../components/shared/comboboxwithmessages/ComboBoxWithMessages.js";
import Form from "react-bootstrap/Form";
import {Button} from "react-bootstrap";
import {InfoCircleFill, PlusLg} from "react-bootstrap-icons";
import React from "react";
import {useTranslation} from "react-i18next";
import {
    getMinutesSinceMidnightFromString,
    getTimeHHmmStringFromMinutesSinceMidnight,
    getTimerDuration
} from "../../../../helpers/helperfunctions.js";

function TimerScheduleListWithWeekGraphic({
    scheduleList,
    updateScheduleListItemProp,
    removeSchedule,
    setDayIsChecked,
    getDayIsChecked,
    addSchedule,
    isAware = false,
    MAX_SCHEDULE_NUM,
    deviceIsSlave,
    masterSerialNumber,
    userCanEdit = true,
    useRoomTemperature = false
}) {
    const { t } = useTranslation(['settingspage', 'common', 'locales']);

    // Helper function to determine border color class
    const getBorderColorClass = (schedule) => {
        if (schedule.messages?.some((m) => m.type === 'error')) return 'border-color-error';
        if (schedule.messages?.some((m) => m.type === 'validation')) return 'border-color-validation';
        if (schedule.inEditMode) return 'border-active';
        if (schedule.isWaitingForUpdate) return 'border-color-warning';
        return 'border-dim';
    };

    // Helper function to render day checkboxes
    const renderDayCheckboxes = (schedule) => {
        const days = [
            { label: t('common:days.mon'), index: 0 },
            { label: t('common:days.tue'), index: 1 },
            { label: t('common:days.wed'), index: 2 },
            { label: t('common:days.thu'), index: 3 },
            { label: t('common:days.fri'), index: 4 },
            { label: t('common:days.sat'), index: 5 },
            { label: t('common:days.sun'), index: 6 },
        ];

        return days.map((day) => (
            <label key={day.index} className="me-2 d-block d-lg-flex align-items-end">
                <span>{day.label}</span>
                <input
                    className="ms-1 mb-4px"
                    type="checkbox"
                    onChange={(e) =>
                        setDayIsChecked(schedule.key, schedule.dayBits, day.index, e.currentTarget.checked)
                    }
                    checked={getDayIsChecked(schedule.dayBits, day.index)}
                    disabled={!(schedule.inEditMode && (schedule.key !== 0 || isAware))}
                />
            </label>
        ));
    };

    // Helper function to handle timer start time changes
    const setTimerStartMinutesSinceMidnight = (schedule, value) => {
        updateScheduleListItemProp(schedule.key, 'timerStartMinutesSinceMidnight', getMinutesSinceMidnightFromString(value));
    };

    // Helper function to handle timer duration changes
    const setTimerDuration = (schedule, value) => {
        updateScheduleListItemProp(
            schedule.key,
            'timerDuration',
            getTimerDuration(schedule.timerStartMinutesSinceMidnight, getMinutesSinceMidnightFromString(value))
        );
    };

    const awareOperationsModeOptionsLanguage = t('locales:schedules_schedule_operation_mode_drop_down_menu')
    
    let awareOperationsModeOptions = []

    awareOperationsModeOptionsLanguage.split(';').forEach((option) => {
        let optionParts = option.split(':')
        if (optionParts.length === 2) {
            awareOperationsModeOptions.push({
                id: Number(optionParts[0].trim()),
                option: optionParts[1]
            })
        } else {
            console.warn("Invalid operation mode option format:", option)
        }
    })


    return (
        <React.Fragment>
            {/* Calendar */}
            <TimeScheduleChart scheduleList={scheduleList} />

            {/* Schedule List */}
            {scheduleList.map((schedule) => (
                <div key={schedule.key}>
                    {userCanEdit && (
                        <div className="d-flex px-2 justify-content-between">
                            <div
                                hidden={deviceIsSlave}
                                className="device-timer-settings-item-anchor"
                                onClick={() =>
                                    updateScheduleListItemProp(schedule.key, 'inEditMode', !schedule.inEditMode)
                                }
                            >
                                <span>
                                    {schedule.inEditMode
                                        ? t('settingspage:device_timers.end_edit')
                                        : t('settingspage:device_timers.edit_schedule')}
                                </span>
                            </div>
                            {(schedule.key !== 0 || isAware) && (
                                <div
                                    className="device-timer-settings-item-anchor"
                                    hidden={deviceIsSlave}
                                    onClick={() => removeSchedule(schedule.key)}
                                >
                                    {t('settingspage:device_timers.delete_schedule')}
                                </div>
                            )}
                        </div>
                    )}

                    <div
                        className={`d-flex d-xs-block border rounded-3 m-2 p-1 p-md-3 justify-content-between ${getBorderColorClass(
                            schedule
                        )}`}
                    >
                        <div className="d-flex align-items-center" style={{ flex: '2' }}>
                            <h5 className="mb-1" style={{ paddingRight: '10px' }}>
                                {schedule.title}
                            </h5>
                            <div className="device-timer-circle" style={{ background: schedule.barColor }}></div>
                        </div>
                        <div className="d-block" style={{ flex: '3' }}>
                            <p className="mb-1">
                                {isAware ? (
                                    <div className="d-flex align-items-center mb-3">
                                        <label className="me-3 mb-0">
                                            {t('settingspage:device_timers.operation_mode')}:
                                        </label>
                                        <ComboBoxWithMessages
                                            disabled={!schedule.inEditMode}
                                            className="me-3"
                                            inputValue={(e) =>
                                                updateScheduleListItemProp(
                                                    schedule.key,
                                                    'mode',
                                                    parseInt(e.currentTarget.value)
                                                )
                                            }
                                            name="operationMode"
                                            value={schedule.mode}
                                            options={awareOperationsModeOptions}
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <span className="me-1">{t('settingspage:device_timers.airflow')}:</span>
                                        {schedule.inEditMode ? (
                                            <input
                                                style={{ width: '48px' }}
                                                onChange={(e) =>
                                                    updateScheduleListItemProp(
                                                        schedule.key,
                                                        'flowPercent',
                                                        parseInt(e.currentTarget.value)
                                                    )
                                                }
                                                type="number"
                                                min="0"
                                                value={schedule.flowPercent}
                                            />
                                        ) : (
                                            <span>{schedule.flowPercent}</span>
                                        )}
                                        <span className="me-3">%</span>

                                        <span className="me-1">
                                            {isAware || useRoomTemperature
                                                ? t('settingspage:device_timers.room_temperature')
                                                : t('settingspage:device_timers.inlet_temperature')}
                                            :
                                        </span>
                                        {schedule.inEditMode ? (
                                            <input
                                                style={{ width: '45px' }}
                                                onChange={(e) =>
                                                    updateScheduleListItemProp(
                                                        schedule.key,
                                                        'temperatureCelcius',
                                                        parseInt(e.currentTarget.value)
                                                    )
                                                }
                                                type="number"
                                                value={schedule.temperatureCelcius}
                                            />
                                        ) : (
                                            <span>{schedule.temperatureCelcius}</span>
                                        )}
                                        <span>°C</span>
                                    </div>
                                )}
                            </p>

                            <p className="mb-1">
                                <span className="me-1">{t('common:general.time')}:</span>
                                {!schedule.inEditMode ? (
                                    <span>
                                        {getTimeHHmmStringFromMinutesSinceMidnight(schedule.timerStartMinutesSinceMidnight)}
                                    </span>
                                ) : (
                                    <input
                                        type="time"
                                        value={getTimeHHmmStringFromMinutesSinceMidnight(
                                            schedule.timerStartMinutesSinceMidnight
                                        )}
                                        onChange={(e) =>
                                            setTimerStartMinutesSinceMidnight(schedule, e.currentTarget.value)
                                        }
                                        min="00:00"
                                        max="24:00"
                                    />
                                )}
                                <span> - </span>
                                {!schedule.inEditMode ? (
                                    <span>
                                        {getTimeHHmmStringFromMinutesSinceMidnight(
                                            schedule.timerStartMinutesSinceMidnight + schedule.timerDuration
                                        )}
                                    </span>
                                ) : (
                                    <input
                                        type="time"
                                        value={getTimeHHmmStringFromMinutesSinceMidnight(
                                            schedule.timerStartMinutesSinceMidnight + schedule.timerDuration
                                        )}
                                        onChange={(e) => setTimerDuration(schedule, e.currentTarget.value)}
                                        min="00:00"
                                        max="24:00"
                                    />
                                )}
                            </p>

                            <div className="d-flex">{renderDayCheckboxes(schedule)}</div>
                        </div>

                        <div className="d-flex align-items-center flex-column justify-content-center d-xs-block p-2">
                            <Form.Label className="mb-0" htmlFor={`${schedule.key}-enabled-switch`}>
                                {schedule.isEnabled ? t('common:general.enabled') : t('common:general.disabled')}
                            </Form.Label>
                            <Form.Switch
                                className="sm color-secondary"
                                name={`${schedule.key}-enabled-switch`}
                                checked={schedule.isEnabled}
                                disabled={!userCanEdit}
                                onChange={(e) =>
                                    updateScheduleListItemProp(schedule.key, 'isEnabled', e.currentTarget.checked)
                                }
                            />
                        </div>
                    </div>

                    {schedule.messages && (
                        <div className="ms-4 mb-1">
                            {schedule.messages.map((m) => (
                                <div key={m.id} className={`color-${m.type}`}>
                                    {m.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {/* Add Schedule Button */}
            {userCanEdit && (
                <div className="d-flex w-100 justify-content-end">
                    <Button
                        className="m-2"
                        onClick={() => addSchedule()}
                        variant="outline-secondary"
                        disabled={scheduleList.length >= MAX_SCHEDULE_NUM + 1 || deviceIsSlave}
                    >
                        <PlusLg className="mb-1 me-1" />
                        {t('settingspage:device_timers.add_schedule')}
                    </Button>
                </div>
            )}

            {/* Info Message for Slave Devices */}
            <div className="alert alert-info" hidden={!deviceIsSlave}>
                <InfoCircleFill className="me-1 mb-1 text-primary" />
                {t('settingspage:slave-device-settings')}
                {masterSerialNumber && (
                    <a href={`/device/${masterSerialNumber}/configurations/timers`}>{masterSerialNumber}</a>
                )}
            </div>
        </React.Fragment>
    );
}

export default TimerScheduleListWithWeekGraphic;